import { useEffect, useMemo, useState } from 'react';
import {
  getTenantUiSettings,
  type RiskIndicatorsUiSettings,
  type RiskSummaryUiSettings,
} from '../services/TenantUiService';

export type UseTenantUiSettingsResult = {
  summary: RiskSummaryUiSettings | null;
  indicators: RiskIndicatorsUiSettings | null;
  indicatorOrder: Record<string, number>;
  isAtlanticLayout: boolean;
  isLoading: boolean;
  error: string | null;
};

function asSummary(raw: Record<string, unknown> | null): RiskSummaryUiSettings | null {
  if (!raw) return null;
  return raw as RiskSummaryUiSettings;
}

function extractIndicatorOrder(raw: Record<string, unknown> | null): Record<string, number> {
  if (!raw) return {};
  const candidate = raw.indicatorOrder ?? raw.indicator_order;
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(candidate as Record<string, unknown>).filter(
      (entry): entry is [string, number] =>
        typeof entry[0] === 'string' &&
        typeof entry[1] === 'number' &&
        !Number.isNaN(entry[1]),
    ),
  );
}

function asIndicators(raw: Record<string, unknown> | null): RiskIndicatorsUiSettings | null {
  if (!raw) return null;
  const { indicatorOrder: _indicatorOrder, indicator_order: _indicatorOrderSnake, ...rest } = raw;
  const columns = rest.columns;
  const normalized: RiskIndicatorsUiSettings = {
    ...(rest as RiskIndicatorsUiSettings),
  };
  if (columns === 1 || columns === 2) {
    normalized.columns = columns;
  } else if (columns !== undefined) {
    delete normalized.columns;
  }
  return normalized;
}

/**
 * Loads tenant UI scopes for Paso 4. Gate: `summary.layoutVariant === 'atlantic-v1'`.
 * Missing settings → legacy layout (isAtlanticLayout false).
 */
export function useTenantUiSettings(enabled = true): UseTenantUiSettingsResult {
  const [summary, setSummary] = useState<RiskSummaryUiSettings | null>(null);
  const [indicators, setIndicators] = useState<RiskIndicatorsUiSettings | null>(null);
  const [indicatorOrder, setIndicatorOrder] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setSummary(null);
      setIndicators(null);
      setIndicatorOrder({});
      setIsLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    const run = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [summaryRes, indicatorsRes] = await Promise.all([
          getTenantUiSettings('risk.summary'),
          getTenantUiSettings('risk.indicators'),
        ]);
        if (cancelled) return;
        setSummary(asSummary(summaryRes.settings));
        setIndicatorOrder(extractIndicatorOrder(indicatorsRes.settings));
        setIndicators(asIndicators(indicatorsRes.settings));
      } catch {
        if (cancelled) return;
        setSummary(null);
        setIndicators(null);
        setIndicatorOrder({});
        setError('Error al cargar configuración de UI del tenant');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const isAtlanticLayout = useMemo(
    () => summary?.layoutVariant === 'atlantic-v1',
    [summary]
  );

  return { summary, indicators, indicatorOrder, isAtlanticLayout, isLoading, error };
}
